import React from "react";
import CurrencyFormat from "react-currency-format";
import DateFormat from "../../helpers/DateFormat";

/**
 * Card face + key-figure summary for the Card Holder Detail page.
 *
 * Added ABOVE the existing 20-field panel rather than replacing it. The old
 * panel weighted every field identically — Balance read exactly like Zip Code
 * — and nothing anchored the page to whose card it is. This surfaces the
 * identity and the five figures an operator actually checks, and the full
 * field list below becomes collapsible.
 *
 * Presentation only: every value is passed in as a prop from data the parent
 * already fetched. No service calls, no state, no conditionals of its own
 * beyond rendering fallbacks for missing values.
 */
const CardHolderSummary = (props) => {
  const {
    cardHolderDetail,
    cardDetails,
    isActive,
    isSuspended,
  } = props;

  const holderName =
    [cardHolderDetail?.firstName, cardHolderDetail?.lastName]
      .filter(Boolean)
      .join(" ") || "—";

  // maskedCardNumber arrives like 5449-23xx-xxxx-3199; show it in groups
  const cardNumber = cardDetails?.maskedCardNumber || "•••• •••• •••• ••••";

  const currency = cardDetails?.cardFeeCurrencyCode || "";

  // Derived from the parent's real state, not from its `Status` variable —
  // that one holds the AVAILABLE ACTION and is inverted (see the note at the
  // call site), so reading it directly reported a live card as SUSPENDED.
  // Suspension is the more important fact, so it wins when both apply.
  const statusLabel = isSuspended
    ? "Suspended"
    : isActive === false || isActive === "false"
    ? "Inactive"
    : "Active";
  const statusTone = statusLabel === "Active" ? "good" : "warn";

  return (
    <div className="bopay-chd-summary">
      <div className="bopay-chd-card">
        <div className="bopay-chd-card-top">
          <span className="bopay-chd-card-brand">bo payments</span>
          <div className="bopay-chd-card-chip" aria-hidden="true">
            <span></span><span></span><span></span>
            <span></span><span></span><span></span>
          </div>
        </div>

        <p className="bopay-chd-card-number">{cardNumber}</p>

        <div className="bopay-chd-card-foot">
          <div>
            <p className="bopay-chd-card-tag">Cardholder</p>
            <p className="bopay-chd-card-name">{holderName}</p>
          </div>
          <div>
            <p className="bopay-chd-card-tag">Expires</p>
            <p className="bopay-chd-card-exp">
              <DateFormat date={cardDetails?.expiryDate} />
            </p>
          </div>
        </div>
      </div>

      <dl className="bopay-chd-figures">
        <div className="bopay-chd-figure">
          <dt>Balance</dt>
          <dd className="bopay-chd-figure-amount">
            <CurrencyFormat
              value={cardHolderDetail?.availableBalance ?? 0}
              displayType={"text"}
              thousandSeparator={true}
            />
            {currency ? <span className="bopay-chd-cur">{currency}</span> : null}
          </dd>
        </div>

        <div className="bopay-chd-figure">
          <dt>Status</dt>
          <dd>
            <span className={"bopay-status bopay-status--" + statusTone}>
              {statusLabel}
            </span>
          </dd>
        </div>

        <div className="bopay-chd-figure">
          <dt>Card type</dt>
          <dd>{cardDetails?.cardTypeName || "—"}</dd>
        </div>

        <div className="bopay-chd-figure">
          <dt>Client</dt>
          <dd>{cardHolderDetail?.companyName || "—"}</dd>
        </div>

        <div className="bopay-chd-figure">
          <dt>Token ID</dt>
          <dd className="bopay-chd-mono">{cardDetails?.tokenId || "—"}</dd>
        </div>
      </dl>
    </div>
  );
};

export default CardHolderSummary;
